import React, { useEffect, useMemo, useState } from 'react';
import {
  addAdminAircraft,
  adminCancelFlight,
  getAdminAircraft,
  getAdminAircraftDetail,
  getAdminHistory,
  getAdminMostFlown,
  getAdminOverview,
  getAdminUserDetail,
  removeAdminAircraft
} from '../services/api';
import AircraftStatusReport from './AircraftStatusReport';
import './AdminPanel.css';

const emptyForm = { registration: '', ac_type: '' };
const PREFIX_OPTIONS = ['', 'HA-', '9H-', 'G-'];

const statusLabel = status => {
  const value = String(status || '').toLowerCase();
  if (value === 'in_progress') return 'On Ground';
  if (value === 'post_flight') return 'Post Flight';
  if (value === 'completed') return 'Completed';
  if (value === 'cancelled') return 'Cancelled';
  return status || 'Unknown';
};

const statusClass = status => `admin-status-${String(status || 'unknown').replace('_', '-')}`;

const formatDateTime = value => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).replace(',', '');
};

const formatNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('en-US') : '0';
};

const formatHours = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : '0.00';
};

const formatMinutesAsHours = value => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '0.00';
  return (number / 60).toFixed(2);
};

const formatDuration = value => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return '--';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return hours ? `${hours}h ${mins}m` : `${mins}m`;
};

const activeAge = minutes => {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total < 0) return '--';
  if (total < 60) return `${Math.round(total)} min`;
  return `${Math.floor(total / 60)}h ${Math.round(total % 60)}m`;
};

const getPilotLabel = item => (
  item?.pilot_name ||
  item?.user_name ||
  item?.top_user_name ||
  item?.pilot_email ||
  item?.email ||
  (item?.user_id ? `User #${item.user_id}` : '--')
);

const ConfirmModal = ({ action, saving, onCancel, onConfirm }) => {
  if (!action) return null;
  return (
    <div className="admin-modal-backdrop" role="presentation" onMouseDown={() => !saving && onCancel()}>
      <div className="admin-modal" role="dialog" aria-modal="true" onMouseDown={event => event.stopPropagation()}>
        <div className="admin-modal-title">{action.title}</div>
        <div className="admin-modal-copy">{action.message}</div>
        <div className="admin-modal-actions">
          <button type="button" className="admin-btn admin-btn-secondary" onClick={onCancel} disabled={saving}>Back</button>
          <button type="button" className="admin-btn admin-btn-danger" onClick={onConfirm} disabled={saving}>
            {saving ? 'Working...' : action.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPanel = ({ onDataChanged }) => {
  const [view, setView] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [aircraft, setAircraft] = useState([]);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [recentHistory, setRecentHistory] = useState([]);
  const [history, setHistory] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [aircraftDetail, setAircraftDetail] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [historyAircraftId, setHistoryAircraftId] = useState('');
  const [historyStatus, setHistoryStatus] = useState('all');
  const [historySearch, setHistorySearch] = useState('');
  const [rankingType, setRankingType] = useState('');
  const [rankingPrefix, setRankingPrefix] = useState('');
  const [rankingSearch, setRankingSearch] = useState('');
  const [fleetSearch, setFleetSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [confirmAction, setConfirmAction] = useState(null);
  const [returnView, setReturnView] = useState('overview');
  const [returnAircraftId, setReturnAircraftId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const activeAircraft = useMemo(
    () => aircraft.filter(item => Number(item.is_active) === 1),
    [aircraft]
  );

  const typeOptions = useMemo(() => (
    [...new Set(aircraftTypes.map(type => String(type || '').trim().toUpperCase()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [aircraftTypes]);

  const filteredAircraft = useMemo(() => {
    const term = fleetSearch.trim().toUpperCase();
    if (!term) return activeAircraft;
    return activeAircraft.filter(item => (
      String(item.registration || '').toUpperCase().includes(term) ||
      String(item.ac_type || '').toUpperCase().includes(term) ||
      String(item.ac_verification_code || '').toUpperCase().includes(term)
    ));
  }, [activeAircraft, fleetSearch]);

  const selectedHistoryAircraft = activeAircraft.find(item => String(item.id) === String(historyAircraftId));

  const loadBaseData = async () => {
    const [overviewRes, aircraftRes, recentRes] = await Promise.all([
      getAdminOverview(),
      getAdminAircraft(),
      getAdminHistory({ status: 'all', limit: 10 })
    ]);
    setOverview(overviewRes.data.data || null);
    setAircraft(aircraftRes.data.aircraft || []);
    setAircraftTypes(aircraftRes.data.types || []);
    setRecentHistory(recentRes.data.flights || []);
  };

  const loadHistory = async (override = {}) => {
    setHistoryLoading(true);
    try {
      const filters = {
        aircraft_id: override.aircraft_id !== undefined ? override.aircraft_id : historyAircraftId,
        status: override.status !== undefined ? override.status : historyStatus,
        search: override.search !== undefined ? override.search : historySearch
      };
      const res = await getAdminHistory({
        aircraft_id: filters.aircraft_id || undefined,
        status: filters.status,
        search: String(filters.search || '').trim() || undefined,
        limit: 250
      });
      setHistory(res.data.flights || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadRanking = async (override = {}) => {
    setRankingLoading(true);
    try {
      const filters = {
        ac_type: override.ac_type !== undefined ? override.ac_type : rankingType,
        prefix: override.prefix !== undefined ? override.prefix : rankingPrefix,
        search: override.search !== undefined ? override.search : rankingSearch
      };
      const res = await getAdminMostFlown({
        ac_type: filters.ac_type || undefined,
        prefix: filters.prefix || undefined,
        search: String(filters.search || '').trim() || undefined,
        limit: 500
      });
      setRanking(res.data.aircraft || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load ranking');
    } finally {
      setRankingLoading(false);
    }
  };

  const loadAircraftDetail = async id => {
    if (!id) return;
    setDetailLoading(true);
    setError('');
    try {
      const res = await getAdminAircraftDetail(id);
      setAircraftDetail(res.data.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load aircraft detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const loadUserDetail = async id => {
    if (!id) return;
    setUserLoading(true);
    setError('');
    try {
      const res = await getAdminUserDetail(id);
      setUserDetail(res.data.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load user detail');
    } finally {
      setUserLoading(false);
    }
  };

  const refreshCurrent = async () => {
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await loadBaseData();
      if (view === 'history') await loadHistory();
      if (view === 'ranking') await loadRanking();
      if (view === 'aircraft' && aircraftDetail?.aircraft?.id) await loadAircraftDetail(aircraftDetail.aircraft.id);
      if (view === 'user' && userDetail?.user?.id) await loadUserDetail(userDetail.user.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === 'history') loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, historyAircraftId, historyStatus]);

  useEffect(() => {
    if (view === 'ranking') loadRanking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, rankingType, rankingPrefix]);

  const setOverviewView = () => {
    setView('overview');
    setReturnAircraftId(null);
  };

  const openRanking = () => {
    setView('ranking');
    setReturnAircraftId(null);
    loadRanking();
  };

  const openHistory = () => {
    setHistoryAircraftId('');
    setHistoryStatus('all');
    setHistorySearch('');
    setView('history');
    setReturnAircraftId(null);
    loadHistory({ aircraft_id: '', status: 'all', search: '' });
  };

  const openAircraft = (item, from = view) => {
    const id = item?.aircraft_id || item?.id;
    if (!id) return;
    setAircraftDetail(null);
    setReturnView(from === 'aircraft' ? 'overview' : from);
    setReturnAircraftId(null);
    setView('aircraft');
    loadAircraftDetail(id);
  };

  const openUser = (userId, from = view) => {
    if (!userId) return;
    setUserDetail(null);
    setReturnView(from === 'user' ? 'overview' : from);
    setReturnAircraftId(view === 'aircraft' ? aircraftDetail?.aircraft?.id || null : null);
    setView('user');
    loadUserDetail(userId);
  };

  const goBack = () => {
    if (view === 'user' && returnAircraftId) {
      setView('aircraft');
      return;
    }
    if (returnView === 'ranking') {
      setView('ranking');
      return;
    }
    if (returnView === 'history') {
      setView('history');
      return;
    }
    setOverviewView();
  };

  const requestCancelFlight = flight => {
    setConfirmAction({
      type: 'cancel-flight',
      item: flight,
      title: 'Cancel active flight',
      message: `Cancel ${flight.flight_number || `flight #${flight.id}`} on ${flight.registration || 'this aircraft'}? This frees the aircraft immediately.`,
      confirmLabel: 'Cancel Flight'
    });
  };

  const requestRemoveAircraft = item => {
    setConfirmAction({
      type: 'remove-aircraft',
      item,
      title: 'Remove aircraft',
      message: `Remove ${item.registration} from the active fleet list? Historic data stays available.`,
      confirmLabel: 'Remove Aircraft'
    });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction?.item) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      if (confirmAction.type === 'cancel-flight') {
        await adminCancelFlight(confirmAction.item.id);
        setMessage(`Flight ${confirmAction.item.flight_number || confirmAction.item.id} cancelled`);
      }
      if (confirmAction.type === 'remove-aircraft') {
        await removeAdminAircraft(confirmAction.item.id);
        setMessage(`${confirmAction.item.registration} removed`);
        if (String(aircraftDetail?.aircraft?.id) === String(confirmAction.item.id)) {
          setOverviewView();
          setAircraftDetail(null);
        }
      }
      setConfirmAction(null);
      await refreshCurrent();
      onDataChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAircraft = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await addAdminAircraft(form);
      setForm(emptyForm);
      setMessage('Aircraft saved');
      await refreshCurrent();
      onDataChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save aircraft');
    } finally {
      setSaving(false);
    }
  };

  const regButton = (item, label, from = view) => (
    <button type="button" className="admin-reg-button" onClick={() => openAircraft(item, from)}>
      {label || item?.registration || '--'}
    </button>
  );

  const userButton = (userId, label, from = view) => {
    if (!userId) return <span className="admin-empty-value">--</span>;
    return (
      <button type="button" className="admin-user-button" onClick={() => openUser(userId, from)}>
        {label || `User #${userId}`}
      </button>
    );
  };

  const field = (label, value) => (
    <div className="admin-field">
      <span>{label}</span>
      <div>{value === null || value === undefined || value === '' ? '--' : value}</div>
    </div>
  );

  const statusPill = status => (
    <span className={`admin-status-pill ${statusClass(status)}`}>{statusLabel(status)}</span>
  );

  const empty = label => <div className="admin-empty-state">{label}</div>;

  const renderHeader = (title, subtitle, actions = null) => (
    <div className="admin-console-header">
      <div>
        <div className="admin-console-subtitle">{subtitle}</div>
        <h1>{title}</h1>
      </div>
      <div className="admin-header-actions">
        {actions}
        {view !== 'overview' ? (
          <button type="button" className="admin-btn admin-btn-secondary" onClick={goBack}>Back</button>
        ) : null}
        <button type="button" className="admin-btn admin-btn-secondary" onClick={refreshCurrent} disabled={loading || saving}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className="admin-tabs" role="tablist" aria-label="Admin views">
      <button type="button" className={view === 'overview' ? 'active' : ''} onClick={setOverviewView}>Overview</button>
      <button type="button" className={view === 'ranking' ? 'active' : ''} onClick={openRanking}>Most Flown</button>
      <button type="button" className={view === 'history' ? 'active' : ''} onClick={openHistory}>History</button>
    </div>
  );

  const renderKpis = stats => (
    <section className="admin-kpi-grid">
      {stats.map(stat => (
        <div className="admin-kpi" key={stat.label}>
          <span>{stat.label}</span>
          <strong>{stat.value}</strong>
        </div>
      ))}
    </section>
  );

  const renderFlightRecord = (flight, options = {}) => (
    <article className="admin-record" key={flight.id}>
      <div className="admin-record-head">
        <div className="admin-record-identity">
          {regButton(flight, flight.registration || '--', options.from || view)}
          <div>
            <strong>{flight.flight_number || `#${flight.id}`}</strong>
            <span>{flight.from_airport || '--'} - {flight.to_airport || '--'}</span>
          </div>
        </div>
        {statusPill(flight.status)}
      </div>
      <div className="admin-field-grid">
        {field('Date', formatDateTime(flight.date_of_flight))}
        {field('Pilot', userButton(flight.user_id, getPilotLabel(flight), options.from || view))}
        {field('Block', formatDuration(flight.block_time))}
        {field('Flight', formatDuration(flight.flight_time))}
        {field('Landings', formatNumber(flight.total_landings))}
        {field('Complaints', formatNumber(flight.complaints_count))}
        {options.cancel ? field('Last Update', activeAge(flight.minutes_since_update)) : null}
      </div>
      {options.cancel ? (
        <div className="admin-record-actions">
          <button type="button" className="admin-btn admin-btn-danger" onClick={() => requestCancelFlight(flight)} disabled={saving}>
            Cancel Flight
          </button>
        </div>
      ) : null}
    </article>
  );

  const renderAircraftRank = (item, from = view) => (
    <article className="admin-record" key={item.id}>
      <div className="admin-record-head">
        <div className="admin-record-identity">
          {regButton(item, item.registration, from)}
          <div>
            <strong>{item.ac_type || '--'}</strong>
            <span>Last flight {formatDateTime(item.last_flight_at)}</span>
          </div>
        </div>
        <div className="admin-top-user">
          <span>Top User</span>
          {userButton(item.top_user_id, item.top_user_name, from)}
        </div>
      </div>
      <div className="admin-field-grid">
        {field('Completed', formatNumber(item.completed_flights))}
        {field('Total', formatNumber(item.total_flights))}
        {field('FH', formatHours(item.flight_hours))}
        {field('FC', formatNumber(item.flight_cycles))}
        {field('Top User Flights', item.top_user_flights ? formatNumber(item.top_user_flights) : '--')}
      </div>
    </article>
  );

  const renderTopUser = item => (
    <article className="admin-record admin-record-tight" key={item.user_id}>
      <div className="admin-record-head">
        <div className="admin-record-identity">
          {userButton(item.user_id, item.user_name, 'aircraft')}
        </div>
      </div>
      <div className="admin-field-grid">
        {field('Total', formatNumber(item.total_flights))}
        {field('Completed', formatNumber(item.completed_flights))}
        {field('BH', formatMinutesAsHours(item.block_minutes))}
        {field('Last', formatDateTime(item.last_flight_at))}
      </div>
    </article>
  );

  const renderFleetManager = () => (
    <section className="admin-section">
      <div className="admin-section-head">
        <div>
          <h2>Manage Aircraft</h2>
          <p>Add aircraft, open records, or remove inactive airframes.</p>
        </div>
        <span>{filteredAircraft.length} shown</span>
      </div>

      <form className="admin-fleet-form" onSubmit={handleAddAircraft}>
        <input
          type="text"
          placeholder="Registration"
          value={form.registration}
          onChange={event => setForm(prev => ({ ...prev, registration: event.target.value }))}
          required
        />
        <select
          value={form.ac_type}
          onChange={event => setForm(prev => ({ ...prev, ac_type: event.target.value }))}
          required
        >
          <option value="">A/C Type</option>
          {typeOptions.map(type => <option value={type} key={type}>{type}</option>)}
        </select>
        <button type="submit" className="admin-btn admin-btn-primary" disabled={saving || typeOptions.length === 0}>
          Add Aircraft
        </button>
      </form>

      <input
        type="text"
        className="admin-search"
        placeholder="Search aircraft"
        value={fleetSearch}
        onChange={event => setFleetSearch(event.target.value)}
      />

      <div className="admin-fleet-list">
        {filteredAircraft.length ? filteredAircraft.map(item => (
          <article className="admin-fleet-card" key={item.id}>
            <div className="admin-record-identity">
              {regButton(item, item.registration, 'overview')}
              <div>
                <strong>{item.ac_type || '--'}</strong>
                <span>{item.ac_verification_code || '--'}</span>
              </div>
            </div>
            <div className="admin-fleet-metrics">
              {field('Flights', formatNumber(item.total_flights))}
              {field('Active', formatNumber(item.active_flights))}
            </div>
            <button
              type="button"
              className="admin-btn admin-btn-danger"
              onClick={() => requestRemoveAircraft(item)}
              disabled={saving || Number(item.active_flights) > 0}
            >
              Remove
            </button>
          </article>
        )) : empty('No aircraft match this search.')}
      </div>
    </section>
  );

  const renderOverview = () => {
    const data = overview || {};
    const summary = data.summary || {};
    const activeFlights = data.activeFlights || [];
    const mostFlown = data.mostFlown || [];

    return (
      <>
        {renderHeader('Operations Control', 'Admin Panel')}
        {renderTabs()}
        {renderKpis([
          { label: 'Active Flights', value: formatNumber(summary.active_flights) },
          { label: 'On Ground', value: formatNumber(summary.on_ground_flights) },
          { label: 'Post Flight', value: formatNumber(summary.post_flight_flights) },
          { label: 'Fleet Aircraft', value: formatNumber(summary.active_aircraft) },
          { label: 'Completed 30d', value: formatNumber(summary.completed_last_30) },
          { label: 'Open Complaints', value: formatNumber(summary.open_complaints) }
        ])}

        <section className="admin-section">
          <div className="admin-section-head">
            <div>
              <h2>Current In-Flight Aircraft</h2>
              <p>Flights currently held by users.</p>
            </div>
            <span>{activeFlights.length}</span>
          </div>
          <div className="admin-record-list">
            {activeFlights.length ? activeFlights.map(flight => renderFlightRecord(flight, { cancel: true, from: 'overview' })) : empty('No active flights')}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <div>
              <h2>Most Flown Aircraft</h2>
              <p>Top 10 aircraft by completed usage.</p>
            </div>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={openRanking}>View All</button>
          </div>
          <div className="admin-record-list">
            {mostFlown.length ? mostFlown.slice(0, 10).map(item => renderAircraftRank(item, 'overview')) : empty('No ranking data')}
          </div>
        </section>

        {renderFleetManager()}

        <section className="admin-section">
          <div className="admin-section-head">
            <div>
              <h2>Detailed Aircraft History</h2>
              <p>Latest 10 flights across the whole fleet.</p>
            </div>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={openHistory}>View All</button>
          </div>
          <div className="admin-record-list">
            {recentHistory.length ? recentHistory.slice(0, 10).map(flight => renderFlightRecord(flight, { from: 'overview' })) : empty('No recent flights')}
          </div>
        </section>
      </>
    );
  };

  const renderRanking = () => (
    <>
      {renderHeader('Most Flown Aircraft', 'Fleet Ranking')}
      {renderTabs()}
      <section className="admin-section">
        <form className="admin-filter-bar" onSubmit={event => { event.preventDefault(); loadRanking(); }}>
          <select value={rankingType} onChange={event => setRankingType(event.target.value)}>
            <option value="">All aircraft types</option>
            {typeOptions.map(type => <option value={type} key={type}>{type}</option>)}
          </select>
          <select value={rankingPrefix} onChange={event => setRankingPrefix(event.target.value)}>
            <option value="">All prefixes</option>
            {PREFIX_OPTIONS.filter(Boolean).map(prefix => <option value={prefix} key={prefix}>{prefix}</option>)}
          </select>
          <input
            type="text"
            placeholder="Search registration"
            value={rankingSearch}
            onChange={event => setRankingSearch(event.target.value)}
          />
          <button type="submit" className="admin-btn admin-btn-secondary" disabled={rankingLoading}>
            {rankingLoading ? 'Loading...' : 'Search'}
          </button>
        </form>
        <div className="admin-record-list">
          {ranking.length ? ranking.map(item => renderAircraftRank(item, 'ranking')) : empty('No ranking data')}
        </div>
      </section>
    </>
  );

  const renderHistory = () => (
    <>
      {renderHeader(selectedHistoryAircraft ? `${selectedHistoryAircraft.registration} History` : 'Detailed Aircraft History', 'Flight Records')}
      {renderTabs()}
      <section className="admin-section">
        <form className="admin-filter-bar" onSubmit={event => { event.preventDefault(); loadHistory(); }}>
          <select value={historyAircraftId} onChange={event => setHistoryAircraftId(event.target.value)}>
            <option value="">All aircraft</option>
            {activeAircraft.map(item => <option value={item.id} key={item.id}>{item.registration}</option>)}
          </select>
          <select value={historyStatus} onChange={event => setHistoryStatus(event.target.value)}>
            <option value="all">All status</option>
            <option value="in_progress">On Ground</option>
            <option value="post_flight">Post Flight</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <input
            type="text"
            placeholder="Search flight, route, pilot"
            value={historySearch}
            onChange={event => setHistorySearch(event.target.value)}
          />
          <button type="submit" className="admin-btn admin-btn-secondary" disabled={historyLoading}>
            {historyLoading ? 'Loading...' : 'Search'}
          </button>
        </form>
        <div className="admin-record-list">
          {history.length ? history.map(flight => renderFlightRecord(flight, { from: 'history' })) : empty('No history records')}
        </div>
      </section>
    </>
  );

  const renderAircraft = () => {
    const detail = aircraftDetail;
    const ac = detail?.aircraft;

    if (detailLoading && !detail) {
      return (
        <>
          {renderHeader('Aircraft Detail', 'Admin')}
          <div className="admin-loading-inline"><div className="spinner" /></div>
        </>
      );
    }

    if (!ac) {
      return (
        <>
          {renderHeader('Aircraft Detail', 'Admin')}
          <section className="admin-section">{empty('Aircraft detail not loaded.')}</section>
        </>
      );
    }

    const removeDisabled = saving || Number(detail.summary?.active_flights || 0) > 0;

    return (
      <>
        {renderHeader(`${ac.registration} Control`, ac.ac_type || 'Aircraft Detail', (
          <button
            type="button"
            className="admin-btn admin-btn-danger"
            onClick={() => requestRemoveAircraft(ac)}
            disabled={removeDisabled}
            title={removeDisabled ? 'Aircraft has an active flight' : 'Remove aircraft'}
          >
            Remove Aircraft
          </button>
        ))}

        {renderKpis([
          { label: 'Open', value: formatNumber(detail.summary.open_items) },
          { label: 'Deferred', value: formatNumber(detail.summary.deferred_items) },
          { label: 'Overdue', value: formatNumber(detail.summary.overdue_items) },
          { label: 'Closed', value: formatNumber(detail.summary.closed_items) },
          { label: 'Flights', value: formatNumber(detail.summary.total_flights) },
          { label: 'Block Hrs', value: formatMinutesAsHours(detail.summary.block_minutes) }
        ])}

        <section className="admin-section">
          <div className="admin-section-head">
            <div>
              <h2>Aircraft Items</h2>
              <p>Open, deferred, overdue, and closed workorders with admin lock/edit controls.</p>
            </div>
          </div>
          <div className="admin-maintenance-frame">
            <AircraftStatusReport
              aircraftId={ac.id}
              showFlyButton={false}
              embeddedMode
              rootTitle="Aircraft Items"
              backLabel="Admin"
              onBack={goBack}
            />
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <div>
              <h2>Most Flown Users</h2>
              <p>Top 10 visible, scroll down for the rest.</p>
            </div>
          </div>
          <div className="admin-scroll-list">
            <div className="admin-record-list">
              {detail.topUsers?.length ? detail.topUsers.map(renderTopUser) : empty('No user flight data')}
            </div>
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <div>
              <h2>Recent Flights</h2>
              <p>Top 10 visible, scroll down for older records.</p>
            </div>
          </div>
          <div className="admin-scroll-list admin-scroll-list-large">
            <div className="admin-record-list">
              {detail.recentFlights?.length ? detail.recentFlights.map(flight => renderFlightRecord(flight, { from: 'aircraft' })) : empty('No recent flights')}
            </div>
          </div>
        </section>
      </>
    );
  };

  const renderUser = () => {
    const detail = userDetail;
    const user = detail?.user;

    if (userLoading && !detail) {
      return (
        <>
          {renderHeader('User Detail', 'Admin')}
          <div className="admin-loading-inline"><div className="spinner" /></div>
        </>
      );
    }

    if (!user) {
      return (
        <>
          {renderHeader('User Detail', 'Admin')}
          <section className="admin-section">{empty('User detail not loaded.')}</section>
        </>
      );
    }

    return (
      <>
        {renderHeader(user.full_name || user.email || `User #${user.id}`, 'User Flight Data')}
        {renderKpis([
          { label: 'Flights', value: formatNumber(detail.summary.total_flights) },
          { label: 'Completed', value: formatNumber(detail.summary.completed_flights) },
          { label: 'Active', value: formatNumber(detail.summary.active_flights) },
          { label: 'Aircraft', value: formatNumber(detail.summary.aircraft_flown) },
          { label: 'Block Hrs', value: formatMinutesAsHours(detail.summary.block_minutes) },
          { label: 'Flight Hrs', value: formatMinutesAsHours(detail.summary.flight_minutes) }
        ])}

        <section className="admin-section">
          <div className="admin-section-head">
            <div>
              <h2>Most Flown Aircraft</h2>
              <p>Aircraft this user has flown most often.</p>
            </div>
          </div>
          <div className="admin-scroll-list">
            <div className="admin-record-list">
              {detail.favoriteAircraft?.length ? detail.favoriteAircraft.map(item => (
                <article className="admin-record admin-record-tight" key={item.id || item.registration}>
                  <div className="admin-record-head">
                    <div className="admin-record-identity">
                      {regButton(item, item.registration, 'user')}
                      <div>
                        <strong>{item.ac_type || '--'}</strong>
                        <span>Last flight {formatDateTime(item.last_flight_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="admin-field-grid">
                    {field('Total', formatNumber(item.total_flights))}
                    {field('Completed', formatNumber(item.completed_flights))}
                    {field('BH', formatMinutesAsHours(item.block_minutes))}
                    {field('FH', formatMinutesAsHours(item.flight_minutes))}
                  </div>
                </article>
              )) : empty('No aircraft data')}
            </div>
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-head">
            <div>
              <h2>Recent Flights</h2>
              <p>Latest flight records for this user.</p>
            </div>
          </div>
          <div className="admin-scroll-list admin-scroll-list-large">
            <div className="admin-record-list">
              {detail.recentFlights?.length ? detail.recentFlights.map(flight => renderFlightRecord(flight, { from: 'user' })) : empty('No recent flights')}
            </div>
          </div>
        </section>
      </>
    );
  };

  if (loading && !overview) {
    return (
      <div className="admin-panel admin-panel-loading">
        <div className="spinner" />
      </div>
    );
  }

  const content = view === 'ranking'
    ? renderRanking()
    : view === 'history'
      ? renderHistory()
      : view === 'aircraft'
        ? renderAircraft()
        : view === 'user'
          ? renderUser()
          : renderOverview();

  return (
    <div className="admin-panel">
      {error ? <div className="admin-alert admin-alert-error">{error}</div> : null}
      {message ? <div className="admin-alert admin-alert-ok">{message}</div> : null}
      {content}
      <ConfirmModal
        action={confirmAction}
        saving={saving}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleConfirmAction}
      />
    </div>
  );
};

export default AdminPanel;
